// web/src/pages/ProfilePublic.tsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Navigate, useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import { toast } from "@/components/ui/use-toast";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  addDoc,
  serverTimestamp,
  getCountFromServer,
  updateDoc,
  type QueryDocumentSnapshot,
  type DocumentData,
  type Timestamp,
  documentId, // <-- added
  onSnapshot,
} from "firebase/firestore";
import { SignIn } from "@/auth/SignIn";
import { claimUsername } from "@/data/username-claim";
import PriceStrip from "@/components/PriceStrip";
import EventCard, { EventDoc } from "@/events/EventCard";
import { openOfferComposer, OfferComposerRoot } from "@/components/offers/OfferComposerModal";
import FollowButtonV3 from "@/components/follow/FollowButtonV3";
import { PriceOverlay } from "@/components/PriceOverlay";
import { deckSlug as buildDeckSlug } from "@/utils/deckSlug"; // ensure util exists
import { ProfileShareModal } from "@/components/ProfileShareModal";

/* ---------------- Types ----------------- */
type BracketId = 1 | 2 | 3 | 4 | 5;
type CompetitiveChoice = "casual" | "competitive" | "ranked";
type FollowPrivacy = "open" | "request" | "private";

type Visibility = {
  display_name: boolean;
  bio: boolean;
  location: boolean;
  play_formats: boolean;
  meetup_types: boolean;
  competitive_preference: boolean;
  commander_tiers: boolean;
  play_radius: boolean;
  trade_radius: boolean;
  available_for_trading: boolean;
  available_for_play: boolean;
  collection_browsing: boolean;
  wishlist_is_public: boolean;
  preferred_lgs?: boolean;
};

type Preferences = {
  available_for_trading?: boolean;
  available_for_play?: boolean;
  collection_browsing?: boolean;
  wishlist_is_public?: boolean;
};

type Presence = {
  show_in_marketplace?: boolean;
  show_in_community_stream?: boolean;
};

type LgsLinks = {
  facebook?: string | null;
  website?: string | null;
  [k: string]: any;
};

type LgsLocation = {
  address?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  lat?: number | null;
  lng?: number | null;
  name?: string | null;
  play_radius?: number | null;
  [k: string]: any;
};

type LgsPreferences = {
  available_for_play?: boolean;
  available_for_trading?: boolean;
  collection_browsing?: boolean;
  [k: string]: any;
};

type LgsInfo = {
  approved?: boolean;
  avatar_url?: string | null;
  email?: string | null;
  external_id?: string | null;
  name?: string | null;
  phone?: string | null;
  source?: string | null;
  updatedAt?: any;
  website?: string | null;
  wpnID?: string | null;
  lgs_seed_last_run?: any;
  links?: LgsLinks;
  location?: LgsLocation;
  preferences?: LgsPreferences;
  services?: string[] | null;
  amenities?: string[] | null;
  weekly_events?: any[] | null;
  supported_formats?: string[] | null;
  [k: string]: any;
};

type ContactMap = {
  email?: string | null;
  phone?: string | null;
  [k: string]: any;
};

type RolesMap = {
  lgsApproved?: boolean;
  [k: string]: any;
};

type Profile = {
  username?: string | null;
  display_name?: string | null;
  displayName?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  avatarUrl?: string | null;
  rank_title?: string | null;

  address?: string | Record<string, any> | null;
  location?: {
    city?: string;
    state?: string;
    lat?: number | null;
    lng?: number | null;
    name?: string | null;
    [k: string]: any;
  };
  play_radius?: number;
  trade_radius?: number;

  play_formats?: string[];
  meetup_types?: string[];
  preferred_lgs?: string;

  competitive_preferences?: CompetitiveChoice[];
  competitive_preference?: CompetitiveChoice;

  commander_brackets?: BracketId[] | null;
  commander_bracket?: BracketId | null;

  preferences?: Preferences;
  presence?: Presence;
  follow_privacy?: FollowPrivacy;
  visibility?: Visibility;

  isLGS?: boolean;
  is_lgs?: boolean;
  email?: string | null;
  contact_email?: string | null;
  contact?: ContactMap | null;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;

  lgs?: LgsInfo | null;

  website?: string | null;
  phone?: string | null;
  links?: LgsLinks | null;
  preferences_map?: LgsPreferences | null;
  roles?: RolesMap | null;

  uid?: string | null;
  [k: string]: any;
};

type UserCard = {
  id: string;
  name: string;
  set?: string;
  number?: string;
  image?: string;
  foil?: boolean;
  condition?: "NM" | "LP" | "MP" | "HP" | "DMG";
  qty?: number;
  status: "sale" | "trade" | "keep";
  price?: number;
  createdAt?: Timestamp;
  scryfall_id?: string;
  type_line?: string; // <--- add
};

type Wish = {
  id: string;
  name: string;
  set?: string;
  number?: string;
  image?: string;
  createdAt?: Timestamp;
};

/* ---------------- Constants ----------------- */
const RESERVED_SLUGS = new Set([
  "", "app", "admin", "login", "logout", "signup", "settings",
  "api", "about", "privacy", "terms", "help", "dashboard", "search",
]);

const BRACKET_LABELS: Record<BracketId, string> = {
  1: "Exhibition",
  2: "Core",
  3: "Upgraded",
  4: "Optimized",
  5: "cEDH",
};

/* ---------------- UI helpers ----------------- */
function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center px-2 py-1 rounded border border-cyan-300/40 text-cyan-200 text-xs bg-cyan-400/10">
      {children}
    </span>
  );
}

function priceLabel(price?: number) {
  if (price == null) return "";
  return `$${Number(price).toFixed(2)}`;
}

// --- Color Identity Renderer (public decks) ---
function renderColorIdentity(colors?: string[] | null) {
  if (!colors || !colors.length) return null;
  const order = ["W","U","B","R","G","C"];
  const sorted = [...new Set(colors)]
    .filter(c => order.includes(c))
    .sort((a,b) => order.indexOf(a) - order.indexOf(b));
  if (!sorted.length) return null;
  return (
    <div className="absolute top-1 right-1 flex gap-[2px] pointer-events-none">
      {sorted.map(c => (
        <span
          key={c}
            className={`w-4 h-4 rounded-full grid place-items-center text-[10px] font-semibold
            ${c==="W"?"bg-amber-200 text-black":
              c==="U"?"bg-sky-400 text-black":
              c==="B"?"bg-neutral-800 text-white":
              c==="R"?"bg-red-600 text-white":
              c==="G"?"bg-emerald-600 text-white":
              "bg-slate-500 text-white"}`}
          title={c}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

// Add helper below renderColorIdentity (different style: pips only, no letters)
function renderBareColorPips(colors?: string[] | null) {
  if (!colors || !colors.length) return null;
  const order = ["W","U","B","R","G","C"];
  const sorted = Array.from(new Set(colors.map(c => c.toUpperCase()).filter(c => order.includes(c))))
    .sort((a,b)=>order.indexOf(a)-order.indexOf(b));
  return (
    <span className="inline-flex items-center gap-[2px] ml-1">
      {sorted.map(c => (
        <span
          key={c}
          className={`w-3.5 h-3.5 rounded-full border border-black/40 shadow-sm`}
          title={c}
          style={{
            background:
              c==="W" ? "#f8e7b5" :
              c==="U" ? "#6ec2ff" :
              c==="B" ? "#313131" :
              c==="R" ? "#d24734" :
              c==="G" ? "#3d8f45" :
              "#7a7f85"
          }}
        />
      ))}
    </span>
  );
}

/* ---------------- Geo helpers ----------------- */
function toNum(v: any): number | null {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

function pickLatLng(obj: any): { lat: number; lng: number } | null {
  if (!obj) return null;
  const paths: Array<[any, any]> = [
    [obj.location?.lat, obj.location?.lng],
    [obj.location?.latitude, obj.location?.longitude],
    [obj.geo?.lat, obj.geo?.lng],
    [obj.geo?.latitude, obj.geo?.longitude],
    [obj.coordinates?.lat, obj.coordinates?.lng],
    [obj.coordinates?.latitude, obj.coordinates?.longitude],
    [obj.lat, obj.lng],
    [obj.latitude, obj.longitude],
  ];
  for (const [la, ln] of paths) {
    const lat = toNum(la);
    const lng = toNum(ln);
    if (lat != null && lng != null) return { lat, lng };
  }
  return null;
}

// Haversine distance in miles
function haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number) {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 3958.7613;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/* ---------------- Component ----------------- */
export default function ProfilePublic() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Support both /username and /@username
  const rawSlug = (params.slug as string) || "";
  const slug = rawSlug.startsWith("@") ? rawSlug.slice(1) : rawSlug;

  // deckSlug from either /:slug/:deckSlug or legacy /@:slug/:deckSlug
  const deckSlugParam =
    typeof params.deckSlug === "string" ? params.deckSlug : undefined;

  // Legacy query param ?deck=<deckId>
  const legacyDeckId = searchParams.get("deck") || undefined;

  // Reserved paths you may want to exclude from username matching
  const reserved = new Set([
    "collection","login","signup","settings","account","trade",
    "offers","inbox","cards","help","privacy","terms","admin"
  ]);
  // Optionally early return if slug collides (not required, but safe)
  // if (reserved.has(slug)) return <NotFound />;

  // Build pretty deck URL (no @ prefix)
  const deckPublicUrl = useCallback(
    (deck: { id: string; name?: string; slug?: string }) => {
      const ds = deck.slug || (deck.name ? buildDeckSlug(deck.name) : deck.id);
      return `/${slug}/${ds}`;
    },
    [slug]
  );

  const [state, setState] = useState<{
    loading: boolean;
    error?: string;
    uid?: string;
    profile?: Profile;
    following?: "none" | "pending" | "active";
  }>({ loading: true });

  const [msg, setMsg] = useState<string | null>(null);

  const currentUid = auth.currentUser?.uid ?? null;
  const isOwnProfile = Boolean(state.uid && currentUid && state.uid === currentUid);

  // Sale/Trade UI state
  const [filter, setFilter] = useState<"all" | "sale" | "trade">("all");
  const [loadingCards, setLoadingCards] = useState(false);
  const [cards, setCards] = useState<UserCard[]>([]);
  // NEW: toggle to hide/show cards that are in decks or binders
  const [hideAssigned, setHideAssigned] = useState(false);

  // Wishlist
  const [loadingWishes, setLoadingWishes] = useState(false);
  const [wishes, setWishes] = useState<Wish[]>([]);
  const lastWishRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMoreWishes, setHasMoreWishes] = useState(false);

  // Totals for PT box
  const [marketTotal, setMarketTotal] = useState<number>(0);
  const [wishlistTotal, setWishlistTotal] = useState<number>(0);

  // Offer modal (legacy fallback only — primary is openOfferComposer)
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerCard, setOfferCard] = useState<UserCard | null>(null);
  const [offerMessage, setOfferMessage] = useState("");
  const [offerPrice, setOfferPrice] = useState<string>("");
  const [submittingOffer, setSubmittingOffer] = useState(false);

  // Quick message modal state
  const [msgOpenModal, setMsgOpenModal] = useState(false);
  const [msgBody, setMsgBody] = useState("");
  const [msgFile, setMsgFile] = useState<File | null>(null);
  const [msgSending, setMsgSending] = useState(false);

  // Events state
  const [events, setEvents] = useState<EventDoc[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventIndex, setEventIndex] = useState(0);
  const [eventDateFilter, setEventDateFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("collection");

  // Distance state
  const [myLoc, setMyLoc] = useState<{ lat: number; lng: number } | null>(null);
  const [distanceMiles, setDistanceMiles] = useState<number | null>(null);

  // Derived profile shorthands
  const p = (state.profile || {}) as Profile;
  const vis: Partial<Visibility> = p.visibility ?? {};
  const tradeOn = Boolean(p.preferences?.available_for_trading);
  const playOn = Boolean(p.preferences?.available_for_play);
  const isLgs = Boolean(
    p.isLGS ||
    p.is_lgs ||
    (p as any)?.roles?.lgsApproved ||
    (p as any)?.lgs?.approved ||
    (p as any)?.roles?.lgs ||
    String((p as any)?.username || "").includes("lgs_")
  );

  const showCollection = Boolean(vis.collection_browsing ?? p?.preferences?.collection_browsing ?? true);
  const showPublicContainers = showCollection; // unify (we hide via excluded set later)

  const showWishlist = playOn && Boolean(p.preferences?.wishlist_is_public);

  // --- Normalize common LGS fields from various possible shapes in user doc ---
  const lgsEmail =
    (p as any).email ||
    (p as any).contact?.email ||
    (p as any).contact_email ||
    (p as any).lgs?.email ||
    null;
  const lgsPhone =
    (p as any).phone ||
    (p as any).contact?.phone ||
    (p as any).contact_phone ||
    (p as any).lgs?.phone ||
    null;
  const lgsWebsite =
    (p as any).website ||
    (p as any).links?.website ||
    (p as any).lgs?.website ||
    (p as any).lgs?.site ||
    null;

  // canonical address string
  const lgsAddressRaw =
    (p as any).address ||
    (p as any).location?.address ||
    (p as any).location?.name ||
    (p as any).lgs?.address ||
    null;

  // normalized store address line
  const storeAddrLine = (() => {
    const a = lgsAddressRaw;
    if (!a) return null;
    if (typeof a === "string") return a;
    if (typeof a === "object") {
      const parts = [
        a.address || a.street || a.address_line1 || a.line1,
        a.city || a.locality || a.town,
        a.state || a.region,
        a.postal_code || a.zip,
      ]
        .filter(Boolean)
        .map((s: any) => String(s).trim());
      return parts.length ? parts.join(", ") : null;
    }
    return String(a);
  })();

  // lat/lng
  const lgsLat =
    toNum((p as any).location?.lat ?? (p as any).location?.latitude ?? (p as any).lat ?? (p as any).lgs?.lat ?? (p as any).lat);
  const lgsLng =
    toNum((p as any).location?.lng ?? (p as any).location?.longitude ?? (p as any).lng ?? (p as any).lgs?.lng ?? (p as any).lng);

  // City/State from address
  const lgsCityState = (() => {
    const s = (lgsAddressRaw || "").toString();
    if (!s) return null;
    const m = s.match(/,\s*([^,]+),\s*([A-Za-z]{2})/);
    if (m) return `${m[1].trim()}/${m[2].trim()}`;
    const parts = s.split(",").map((t) => t.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const city = parts.length >= 3 ? parts[parts.length - 3] : parts[0];
      const stateToken = parts[parts.length - 2];
      const st = stateToken ? stateToken.split(/\s+/)[0] : "";
      return `${city}/${st || stateToken}`;
    }
    const m2 = s.match(/([A-Za-z\s]+)\s([A-Z]{2})\s*\d{5}/);
    if (m2) return `${m2[1].trim()}/${m2[2].trim()}`;
    return null;
  })();

// --- ADD BELOW THIS LINE (missing derived header vars) ---
  const headerName =
    p.display_name ||
    p.displayName ||
    p.username ||
    (state.uid ? `@${slug}` : "Profile");

  const locationString = (() => {
    if (isLgs && storeAddrLine) return storeAddrLine;
    const city = p.location?.city;
    const st = p.location?.state;
    if (city && st) return `${city}, ${st}`;
    if (city) return city;
    return null;
  })();

  const rankLabel = p.rank_title || "Unranked";

  const competitive: CompetitiveChoice[] = Array.isArray(p.competitive_preferences)
    ? (p.competitive_preferences as CompetitiveChoice[])
    : p.competitive_preference
    ? [p.competitive_preference]
    : [];

  const commanderBrackets: BracketId[] = Array.isArray(p.commander_brackets)
    ? (p.commander_brackets.filter((b: any) => [1,2,3,4,5].includes(b)) as BracketId[])
    : p.commander_bracket && [1,2,3,4,5].includes(p.commander_bracket)
    ? [p.commander_bracket]
    : [];

  const showCompetitive = competitive.length > 0;
  const showFormats = Array.isArray(p.play_formats) && p.play_formats.length > 0;
  const showCommander = commanderBrackets.length > 0;
  const showMeetups = Array.isArray(p.meetup_types) && p.meetup_types.length > 0;
  const showLgs = !!p.preferred_lgs;
// --- END ADD ---

// === Public binder/deck mirror state (ADD) ===
  const [pubBinders, setPubBinders] = useState<Array<{ id: string; name: string; count: number }>>([]);
  const [pubDecks, setPubDecks] = useState<Array<{
    id: string;
    name: string;
    count: number;
    commanderId?: string | null;
    commanderSnapshot?: any;
    colors?: string[] | null;
    slug?: string;
    coverImage?: string | null;
  }>>([]);
  const [binderCards, setBinderCards] = useState<Record<string, UserCard[]>>({});
  const [deckCards, setDeckCards] = useState<Record<string, UserCard[]>>({});


  // NEW: assignment & UI control state
  const [binderAssignments, setBinderAssignments] = useState<Record<string, string[]>>({});
  const [deckAssignments, setDeckAssignments]     = useState<Record<string, string[]>>({});
  const [excludedCardIds, setExcludedCardIds]     = useState<Set<string>>(new Set());
  const [expandedBinders, setExpandedBinders]     = useState<Record<string, boolean>>({});
  const [expandedDecks, setExpandedDecks]         = useState<Record<string, boolean>>({});

  // ---------- Resolve username → uid + profile ----------
  useEffect(() => {
    (async () => {
      if (!slug) return setState({ loading: false, error: "no-username" });
      if (RESERVED_SLUGS.has(slug)) return setState({ loading: false, error: "reserved" });

      const unameSnap = await getDoc(doc(db, "usernames", slug));
      if (!unameSnap.exists()) return setState({ loading: false, error: "not-found" });

      const uid = (unameSnap.data() as any)?.uid as string | undefined;
      if (!uid) return setState({ loading: false, error: "not-found" });

      const profileSnap = await getDoc(doc(db, "users", uid));
      // prefer users/{uid} but also merge profiles/{uid}
      let profile = profileSnap.exists() ? (profileSnap.data() as Profile) : ({} as Profile);
      try {
        const extraSnap = await getDoc(doc(db, "profiles", uid));
        if (extraSnap.exists()) {
          profile = { ...profile, ...(extraSnap.data() as any) };
        }
      } catch {}

      setState({ loading: false, uid, profile });
    })().catch((e) => setState({ loading: false, error: e?.message || "error" }));
  }, [slug]);

  /* ---------- Events (hosted by this profile) ---------- */
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!state.uid) { if (alive) setEvents([]); return; }
      setEventsLoading(true);
      try {
        const isOwner = auth.currentUser?.uid === state.uid;
        const col = collection(db, "events");

        const build = (field: string, vis?: "public" | "private") => {
          const parts: any[] = [where(field, "==", state.uid)];
            if (!isOwner) parts.push(where("visibility", "==", "public"));
            if (vis) parts.push(where("visibility", "==", vis));
          return query(col, ...parts);
        };

        const q1 = build("organizerUid");
        const q2 = build("ownerUid");
        const q3 = build("event_host_uid");

        const privQs = isOwner
          ? [build("organizerUid", "private"), build("ownerUid", "private"), build("event_host_uid", "private")]
          : [];

        const results = await Promise.allSettled([
          getDocs(q1), getDocs(q2), getDocs(q3),
          ...privQs.map(q => getDocs(q)),
        ]);

        if (!alive) return;
        const map = new Map<string, EventDoc>();

        for (const res of results) {
          if (res.status !== "fulfilled") {
            console.warn("[ProfilePublic] events query failed:", (res as any)?.reason);
            continue;
          }
          for (const d of res.value.docs) {
            const v: any = d.data();
            const id = d.id;
            if (map.has(id)) continue;
            map.set(id, {
              id,
              title: v.title ?? v.name ?? "Event",
              details: v.details ?? v.description ?? "",
              formatTypes: Array.isArray(v.formatTypes) ? v.formatTypes : [],
              visibility: v.visibility === "private" ? "private" : "public",
              startTime: v.startTime ?? v.start ?? v.startAt ?? v.start_at ?? null,
              endTime: v.endTime ?? v.endAt ?? null,
              organizerUid: v.organizerUid ?? v.ownerUid ?? v.event_host_uid ?? "",
              organizerUsername: v.organizerUsername ?? v.organizer?.username ?? "",
              organizerDisplayName: v.organizerDisplayName ?? v.organizer?.displayName ?? null,
              organizerAvatarUrl: v.organizerAvatarUrl ?? v.organizer?.avatar_url ?? null,
              organizerIsLGS: Boolean(v.organizerIsLGS || (v.locationType === "LGS")),
              images: Array.isArray(v.images) ? v.images : (v.images ? [v.images] : []),
              distanceMiles: typeof v.distanceMiles === "number"
                ? v.distanceMiles
                : (typeof v.distanceMi === "number" ? v.distanceMi : null),
              favoritesCount: typeof v.favoritesCount === "number" ? v.favoritesCount : 0,
              eventType: v.eventType ?? null,
              address: v.address ?? null,
              location: v.location ?? null,
            } as EventDoc);
          }
        }

        const toMillis = (val: any) =>
          val?.toDate?.() instanceof Date ? val.toDate().getTime()
          : val instanceof Date ? val.getTime()
          : typeof val === "number" ? val
          : typeof val === "string" ? Date.parse(val)
          : 0;

        const list = Array.from(map.values()).sort((a, b) => toMillis(a.startTime) - toMillis(b.startTime));
        setEvents(list);
        setEventIndex(0);
      } catch (e: any) {
        console.warn("[ProfilePublic] failed to load events:", e?.message || e);
        setEvents([]);
      } finally {
        if (alive) setEventsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [state.uid]);

 // --- Helper: fetch a batch of card docs by IDs (only for IDs not already in cards state) ---
  async function fetchMissingCardDocs(ownerUid: string, ids: string[]): Promise<UserCard[]> {
  const existingSet = new Set(cards.map(c => c.id));
  const need = ids.filter(id => !existingSet.has(id));
  if (!need.length) return [];
  const out: UserCard[] = [];
  for (let i = 0; i < need.length; i += 10) {
    const slice = need.slice(i, i + 10);
    try {
      const qy = query(
        collection(db, "users", ownerUid, "cards"),
        where(documentId(), "in", slice)
      );
      const snap = await getDocs(qy);
      snap.forEach(d => {
        const v: any = d.data();
        // Treat card as public if (status sale/trade) OR (for_sale / for_trade true)
        const isPublic =
          v?.status === "sale" ||
          v?.status === "trade" ||
          v?.for_sale === true ||
          v?.for_trade === true;
        if (!isPublic) return;
        out.push({
          id: d.id,
          name: v.name ?? "Unknown",
          set: v.set,
          number: v.number,
          image: v.image,
          foil: !!v.foil,
            condition: v.condition,
          qty: v.qty ?? 1,
          status: v.status === "sale" || v.status === "trade"
            ? v.status
            : (v.for_sale ? "sale" : "trade"), // map legacy flags
          price: v.price,
          createdAt: v.createdAt,
          scryfall_id: v.scryfall_id,
          type_line: v.type_line || v.scryfall_full?.type_line || undefined,
        });
      });
    } catch (e) {
      if (import.meta.env.DEV) console.debug("[fetchMissingCardDocs] batch failed", e);
    }
  }
  if (out.length) {
    setCards(prev => {
      const map = new Map(prev.map(c => [`${c.id}:${c.status}`, c]));
      out.forEach(c => map.set(`${c.id}:${c.status}`, c));
      return Array.from(map.values()).sort((a, b) => {
        const am = (a.createdAt as any)?.toMillis?.() ?? 0;
        const bm = (b.createdAt as any)?.toMillis?.() ?? 0;
        return bm - am;
      });
    });
  }
  return out;
}

  // --- REALTIME public binder & deck listeners (mirror of MyCollection logic, but public) ---
  useEffect(() => {
    if (!state.uid) return;
    // Clean slates
    setPubBinders([]);
    setPubDecks([]);
    setBinderAssignments({});
    setDeckAssignments({});
    setBinderCards({});
    setDeckCards({});
    setExpandedBinders({});
    setExpandedDecks({});
    setExcludedCardIds(new Set());

    const unsubs: Array<() => void> = [];

    // Track slot maps (internal)
    const binderSlotMap = new Map<string, Set<string>>();
    const deckSlotMap   = new Map<string, Set<string>>();

    function rebuildExcluded() {
      const excl = new Set<string>();
      for (const set of binderSlotMap.values()) set.forEach(id => excl.add(id));
      for (const set of deckSlotMap.values()) set.forEach(id => excl.add(id));
      setExcludedCardIds(excl);
    }

    // Binders metadata
    unsubs.push(
      onSnapshot(
        collection(db, "users", state.uid, "public_binders"),
        snap => {
          const list = snap.docs.map(d => {
            const v: any = d.data() || {};
            return {
              id: d.id,
              name: v.name || v.title || "Binder",
              count: binderSlotMap.get(d.id)?.size || 0,
            };
          });
          setPubBinders(list);
        },
        err => console.warn("[ProfilePublic] public_binders listen failed:", err)
      )
    );

    // Binder assignments (slots)
    unsubs.push(
      onSnapshot(
        collection(db, "users", state.uid, "public_binder_assignments"),
        snap => {
          binderSlotMap.clear();
          snap.forEach(d => {
            const v: any = d.data() || {};
            const binderId = v.binderId || v.binder_id;
            const cardId = v.cardId || v.card_id || v.userCardId || v.user_card_id || null;
            if (!binderId || !cardId) return;
            let set = binderSlotMap.get(binderId);
            if (!set) {
              set = new Set();
              binderSlotMap.set(binderId, set);
            }
            set.add(String(cardId));
          });
          const obj: Record<string, string[]> = {};
          binderSlotMap.forEach((set, b) => (obj[b] = Array.from(set)));
          setBinderAssignments(obj);
          setPubBinders(prev =>
            prev.map(b => ({
              ...b,
              count: binderSlotMap.get(b.id)?.size || 0,
            }))
          );
          rebuildExcluded();
        },
        err => console.warn("[ProfilePublic] public_binder_assignments listen failed:", err)
      )
    );

    // Deck metadata
     unsubs.push(
      onSnapshot(
        collection(db, "users", state.uid, "public_decks"),
        snap => {
          const list = snap.docs.map(d => {
            const v: any = d.data() || {};
            const rawColors =
              v.colorIdentity ??
              v.color_identity ??
              v.colors ??
              v.identity ??
              v.color_identity_codes ??
              null;

            const normalizeColors = (raw: any): string[] | null => {
              if (!raw) return null;
              const mapName = (s: string) => {
                const up = s.trim().toUpperCase();
                if (["W","WHITE"].includes(up)) return "W";
                if (["U","BLUE"].includes(up)) return "U";
                if (["B","BLACK"].includes(up)) return "B";
                if (["R","RED"].includes(up)) return "R";
                if (["G","GREEN"].includes(up)) return "G";
                if (["C","COLORLESS"].includes(up)) return "C";
                return null;
              };
              let parts: string[] = [];
              if (Array.isArray(raw)) parts = raw.map(String);
              else if (typeof raw === "string") {
                if (/[^a-zA-Z]/.test(raw)) parts = raw.split(/[^a-zA-Z]/).filter(Boolean);
                else parts = raw.split("");
              } else if (typeof raw === "object") {
                parts = Object.keys(raw);
              }
              const norm = parts.map(mapName).filter(Boolean) as string[];
              if (!norm.length) return null;
              const order = ["W","U","B","R","G","C"];
              return Array.from(new Set(norm)).sort((a,b)=>order.indexOf(a)-order.indexOf(b));
            };

            const colors = normalizeColors(rawColors);

            const commanderObj = (typeof v.commander === "object" && v.commander && !Array.isArray(v.commander)) ? v.commander : null;
            const commanderId: string | null =
              v.commanderUserCardId ||
              v.commander_card_user_id ||
              v.commanderCardUserId ||
              v.commanderCardId ||
              v.commander_card_id ||
              v.primaryCommanderId ||
              v.commanderId ||
              (typeof v.commander === "string" && v.commander) ||
              commanderObj?.userCardId ||
              commanderObj?.cardId ||
              commanderObj?.id ||
              null;

            const commanderSnapshot =
              commanderObj ||
              v.commanderSnapshot ||
              v.commander_snapshot ||
              v.commanderCard ||
              v.commander_card ||
              null;

            return {
              id: d.id,
              name: v.name || v.title || "Deck",
              count: deckSlotMap.get(d.id)?.size || 0,
              commanderId,
              commanderSnapshot,
              colors,
              slug: v.slug || v.name
                ? String(v.name || d.id)
                    .toLowerCase()
                    .trim()
                    .replace(/['"]/g, "")
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "")
                    .slice(0, 48)
                : d.id,
              coverImage: v.coverImage || v.coverCardImage || v.cover_card_image || null,
              coverCardId: v.coverCardId || null,
              format: v.format || v.deckFormat || v.type || null, // <-- added
            };
          });
          setPubDecks(list);
        },
        err => console.warn("[ProfilePublic] public_decks listen failed:", err)
      )
    );

    // Deck slots
    unsubs.push(
      onSnapshot(
        collection(db, "users", state.uid, "public_deck_slots"),
        snap => {
          deckSlotMap.clear();
          snap.forEach(d => {
            const v: any = d.data() || {};
            const deckId = v.deckId || v.deck_id;
            const cardId = v.cardId || v.card_id || v.userCardId || v.user_card_id || null;
            if (!deckId || !cardId) return;
            let set = deckSlotMap.get(deckId);
            if (!set) {
              set = new Set();
              deckSlotMap.set(deckId, set);
            }
            set.add(String(cardId));
          });
          const obj: Record<string, string[]> = {};
          deckSlotMap.forEach((set, d) => (obj[d] = Array.from(set)));
          setDeckAssignments(obj);
          setPubDecks(prev =>
            prev.map(d => ({
              ...d,
              count: deckSlotMap.get(d.id)?.size || 0,
            }))
          );
          rebuildExcluded();
        },
        err => console.warn("[ProfilePublic] public_deck_slots listen failed:", err)
      )
    );

    return () => unsubs.forEach(u => u());
  }, [state.uid, cards]);

  // --- Build binder preview cards (up to 9) whenever assignments or cards change ---
  useEffect(() => {
    if (!state.uid) return;
    const next: Record<string, UserCard[]> = { ...binderCards };
    let changed = false;

    for (const [binderId, ids] of Object.entries(binderAssignments)) {
      const want = ids.slice(0, 9);
      const have = next[binderId]?.map(c => c.id) || [];
      const missing = want.filter(id => !have.includes(id));
      if (missing.length) {
        changed = true;
      }
    }

    async function hydrate() {
      const allMissing: string[] = [];
      for (const ids of Object.values(binderAssignments)) {
        const previewIds = ids.slice(0, 9);
        previewIds.forEach(id => {
          if (!cards.some(c => c.id === id)) allMissing.push(id);
        });
      }
      if (allMissing.length) {
        await fetchMissingCardDocs(state.uid!, Array.from(new Set(allMissing)));
      }
      const rebuilt: Record<string, UserCard[]> = {};
      for (const [binderId, ids] of Object.entries(binderAssignments)) {
        const previewIds = ids.slice(0, 9);
        rebuilt[binderId] = previewIds
          .map(id => cards.find(c => c.id === id))
          .filter(Boolean) as UserCard[];
      }
      setBinderCards(rebuilt);
    }

    if (changed) void hydrate();
    else {
      // Still rebuild to sync counts if card images updated
      const rebuilt: Record<string, UserCard[]> = {};
      for (const [binderId, ids] of Object.entries(binderAssignments)) {
        rebuilt[binderId] = ids
          .slice(0, 9)
          .map(id => cards.find(c => c.id === id))
          .filter(Boolean) as UserCard[];
      }
      setBinderCards(rebuilt);
    }
  }, [binderAssignments, cards]);

  // --- Build deck highlight + full (on expand) ---
  useEffect(() => {
  if (!state.uid) return;
  const toFetch: string[] = [];
  for (const meta of pubDecks) {
    const ids = deckAssignments[meta.id] || [];
    const candidate = meta.commanderId || ids[0];
    if (candidate && !cards.some(c => c.id === candidate)) toFetch.push(candidate);
  }

  async function hydrate() {
    if (toFetch.length) {
      await fetchMissingCardDocs(state.uid!, Array.from(new Set(toFetch)));
    }
    const rebuilt: Record<string, UserCard[]> = {};
    for (const meta of pubDecks) {
      const ids = deckAssignments[meta.id] || [];
      let highlight: UserCard | null = null;

      // prefer real commander card
      if (meta.commanderId) {
        highlight = cards.find(c => c.id === meta.commanderId) || null;
      }
      // fallback pseudo from snapshot
      if (!highlight && meta.commanderId && meta.commanderSnapshot) {
        const s = meta.commanderSnapshot;
        highlight = {
          id: meta.commanderId,
          name: s.name || s.cardName || "Commander",
          set: s.set || s.expansion || undefined,
          number: s.number || s.collector_number || undefined,
          image: s.image || s.img || s.imageUrl || s.art || "/images/cardback.png",
          foil: !!(s.foil ?? s.isFoil),
          condition: s.condition || undefined,
          qty: 1,
          status: "trade",
          price: s.price ?? s.listingPrice ?? undefined,
          createdAt: s.createdAt || s.updatedAt || undefined,
          scryfall_id: s.scryfall_id || s.scryfallId || s.id || undefined,
        };
      }
      // final fallback: first slot
      if (!highlight) {
        const first = ids[0];
        if (first) highlight = cards.find(c => c.id === first) || null;
      }
      rebuilt[meta.id] = highlight ? [highlight] : [];
    }
    setDeckCards(prev => ({ ...prev, ...rebuilt }));
  }
  void hydrate();
}, [pubDecks, deckAssignments, cards, state.uid]);

// === Realtime listener for all public sale/trade cards (full collection, then filter) ===
useEffect(() => {
  if (!state.uid || !showCollection) return;
  setLoadingCards(true);

  const colRef = collection(db, "users", state.uid, "cards");
  const unsub = onSnapshot(
    colRef,
    snap => {
      const list: UserCard[] = [];
      snap.forEach(d => {
        const v: any = d.data() || {};
        // Determine public exposure
        const isSale   = v.status === "sale"  || v.for_sale === true;
        const isTrade  = v.status === "trade" || v.for_trade === true;
        if (!isSale && !isTrade) return;

        // Normalize status preference: explicit status wins, else legacy flags
        const normStatus: "sale" | "trade" =
          v.status === "sale" ? "sale" :
          v.status === "trade" ? "trade" :
          (v.for_sale ? "sale" : "trade");

        list.push({
          id: d.id,
          name: v.name ?? "Unknown",
          set: v.set ?? undefined,
          number: v.number ?? undefined,
          image: v.image ?? undefined,
          foil: !!v.foil,
          condition: v.condition,
          qty: v.qty ?? 1,
          status: normStatus,
          price: v.price,
          createdAt: v.createdAt,
          scryfall_id: v.scryfall_id,
          type_line: v.type_line || v.scryfall_full?.type_line || undefined,
        });
      });

      // Sort newest first
      list.sort((a, b) => {
        const am = (a.createdAt as any)?.toMillis?.() ?? 0;
        const bm = (b.createdAt as any)?.toMillis?.() ?? 0;
        return bm - am;
      });

      setCards(list);
      setLoadingCards(false);
    },
    err => {
      console.warn("[ProfilePublic] cards realtime listener failed:", err);
      setLoadingCards(false);
    }
  );
  return () => unsub();
}, [state.uid, showCollection]);

  // --- Adjust visible market cards to hide any in binder/deck (like MyCollection) ---
  // All sale/trade cards stream in `cards`; filter out those assigned to any binder/deck via excludedCardIds and apply user filter
  const visibleMarketCards = useMemo(() => {
    return cards.filter(c => {
      if (hideAssigned && excludedCardIds.has(c.id)) return false; // only hide when toggle ON
      if (filter === "all") return true;
      if (filter === "sale") return c.status === "sale";
      return c.status === "trade";
    });
  }, [cards, excludedCardIds, filter, hideAssigned]);

  const filteredEvents = useMemo(() => {
    if (!eventDateFilter) return events;
    const target = new Date(eventDateFilter);
    if (isNaN(target.getTime())) return events;

    const toDate = (x: any): Date | null =>
      x?.toDate?.() ??
      (x instanceof Date ? x :
        (typeof x === "number" ? new Date(x) :
          (typeof x === "string" ? new Date(Date.parse(x)) : null)));

    return events.filter((ev: any) => {
      const d = toDate(ev.startTime);
      if (!d || isNaN(d.getTime())) return false;
      return (
        d.getFullYear() === target.getFullYear() &&
        d.getMonth() === target.getMonth() &&
        d.getDate() === target.getDate()
      );
    });
  }, [events, eventDateFilter]);

  useEffect(() => {
    if (filteredEvents.length === 0) setEventIndex(0);
    else if (eventIndex >= filteredEvents.length) setEventIndex(0);
  }, [filteredEvents, eventIndex]);

  /* ---------- Cards / Wishlist loaders ---------- */
 

  async function loadWishes(initial = false) {
    if (!state.uid) return;
    setLoadingWishes(true);
    try {
      const root = collection(db, "users", state.uid, "wishlist");
      const pageSize = 30;

      const qy = lastWishRef.current
        ? query(root, orderBy("createdAt", "desc"), startAfter(lastWishRef.current), limit(pageSize))
        : query(root, orderBy("createdAt", "desc"), limit(pageSize));

      const snap = await getDocs(qy);
      const docs = snap.docs as QueryDocumentSnapshot<DocumentData>[];

      if (docs.length > 0) lastWishRef.current = docs[docs.length - 1];

      const list = docs.map((d) => {
        const v = d.data();
        return {
          id: d.id,
          name: v.name ?? "Unknown",
          set: v.set ?? undefined,
          number: v.number ?? undefined,
          image: v.image ?? undefined,
          createdAt: v.createdAt,
        } as Wish;
      });

      setWishes(initial ? list : wishes.concat(list));
      setHasMoreWishes(docs.length === pageSize);
    } finally {
      setLoadingWishes(false);
    }
  }

  // PT totals
  useEffect(() => {
    (async () => {
      if (!state.uid) return;
      const cardsCol = collection(db, "users", state.uid, "cards");
      const wishlistCol = collection(db, "users", state.uid, "wishlist");
      try {
        const [saleCnt, tradeCnt, wishCnt] = await Promise.all([
          getCountFromServer(query(cardsCol, where("status", "==", "sale"))),
          getCountFromServer(query(cardsCol, where("status", "==", "trade"))),
          getCountFromServer(query(wishlistCol)),
        ]);
        setMarketTotal((saleCnt.data().count || 0) + (tradeCnt.data().count || 0));
        setWishlistTotal(wishCnt.data().count || 0);
      } catch {
        setMarketTotal((cards ?? []).length);
        setWishlistTotal((wishes ?? []).length);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.uid]);

  useEffect(() => {
    if (!state.uid) return;
    // Reset only wishlist pagination; cards now stream via listener
    lastWishRef.current = null;
    if (showWishlist) void loadWishes(true);
  }, [state.uid, showCollection, showWishlist]);

  /* ---------- Messaging & share ---------- */
  const handleMessage = () => {
    if (!state.uid) return;
    if (!auth.currentUser) {
      toast({ description: "Sign in to send a message." });
      return;
    }
    setMsgOpenModal(true);
  };

  const openShareProfile = useCallback(() => {
    setShowProfileShare(true);
  }, []);
const [showProfileShare, setShowProfileShare] = useState(false);
  /* ---------- Quick message send ---------- */
  async function sendQuickMessage() {
    if (!auth.currentUser || !state.uid) return;
    const me = auth.currentUser.uid;
    const them = state.uid;
    if (me === them) return;

    const text = (msgBody || "").trim();
    if (!text && !msgFile) {
      toast({ description: "Type a message or attach a file." });
      return;
    }

    setMsgSending(true);
    try {
      // find existing thread
      let threadId: string | null = null;
      try {
        const threadsQ = query(collection(db, "threads_v2"), where("participants", "array-contains", me));
        const snap = await getDocs(threadsQ);
        for (const d of snap.docs) {
          const v: any = d.data();
          const ps: string[] = Array.isArray(v.participants) ? v.participants : [];
          if (ps.includes(them)) { threadId = d.id; break; }
        }
      } catch (e) {
        console.warn("Conversation lookup failed, creating new.", e);
      }

      // create if missing
      if (!threadId) {
        const threadDoc = await addDoc(collection(db, "threads_v2"), {
          participants: [me, them],
          visibleFor: [me, them],
          updatedAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
          lastMessage: null,
          lastSenderId: null,
          messageCount: 0,
        });
        threadId = threadDoc.id;
      }

      // optional attachment upload
      let attachmentUrl: string | null = null;
      if (msgFile) {
        try {
          const { getStorage, ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
          const storage = getStorage();
          const key = `messages/${me}/${Date.now()}-${msgFile.name}`;
          const fileRef = ref(storage, key);
          await uploadBytes(fileRef, msgFile);
          attachmentUrl = await getDownloadURL(fileRef);
        } catch (e) {
          console.warn("Attachment upload failed; sending without attachment.", e);
        }
      }

      // post message
      const payload: any = {
        threadId,
        senderId: me,
        recipientId: them,
        participants: [me, them],
        body: text || (attachmentUrl ? "(attachment)" : ""),
        createdAt: serverTimestamp(),
        isRead: false,
      };
      if (attachmentUrl) payload.attachmentUrls = [attachmentUrl];

      await addDoc(collection(db, "messages_v2"), payload);

      // update thread metadata
      try {
        await updateDoc(doc(db, "threads_v2", threadId), {
          updatedAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
          lastMessage: (text || "📎 Attachment").slice(0, 240),
          lastSenderId: me,
        });
      } catch (e) {
        console.warn("Thread metadata update failed (non-fatal):", e);
      }

      // notify recipient
      try {
        await addDoc(collection(db, "users", them, "notifications"), {
          toUid: them,
          fromUid: me,
          type: "message",
          text: text || "Attachment",
          conversationId: threadId,
          createdAt: serverTimestamp(),
          read: false,
          isRead: false,
        });
      } catch (e) {
        console.warn("Notification create failed (non-fatal):", e);
      }

      setMsgOpenModal(false);
      setMsgBody("");
      setMsgFile(null);
      toast({ description: `Message sent to @${p.username || slug}` });
    } catch (e) {
      console.error(e);
      toast({ description: "Could not send message. Try again." });
    } finally {
      setMsgSending(false);
    }
  }

  /* ---------- Offer flow (unified composer + legacy fallback) ---------- */
  function openOffer(c: UserCard) {
  const DEV = import.meta.env.DEV;

  if (!auth.currentUser) {
    toast({ description: "Sign in to send an offer." });
    return;
  }
  if (isOwnProfile || !state.uid) {
    if (DEV) console.debug("[openOffer] abort (own profile or no uid)");
    return;
  }
  if (!c || !c.id) {
    if (DEV) console.warn("[openOffer] invalid card", c);
    return;
  }

  const cardPayload = {
    cardId: c.id,
    name: c.name,
    image: c.image || null,
    set: c.set || null,
    number: c.number ?? null,
    price: c.status === "sale" ? (typeof c.price === "number" ? c.price : null) : null,
    foil: !!c.foil,
    condition: c.condition || null,
    status: c.status,
    scryfall_id: c.scryfall_id || null,
  };

  const payload = {
    toUid: state.uid,
    toUsername: p.username || p.display_name || `user-${state.uid.slice(0,6)}`,
    toAvatarUrl: p.avatar_url || null,
    toAvatar: p.avatar_url || null, // legacy alias
    card: cardPayload,
  };

  // Restore event-dispatch workflow for the composer
  try {
    const ev1 = new CustomEvent("offer:open", { detail: payload });
    const ev2 = new CustomEvent("OfferComposerModal:open", { detail: payload });
    window.dispatchEvent(ev1);
    window.dispatchEvent(ev2);
  } catch (e) {
    if (DEV) console.warn("[openOffer] event dispatch failed", e);
  }

  // Keep function fallback for newer composer hook
  try {
    if (typeof openOfferComposer === "function") openOfferComposer(payload);
  } catch (e) {
    if (DEV) console.warn("[openOffer] function fallback failed", e);
  }

  // Probe for dialog; fallback to legacy modal if not detected
  setTimeout(() => {
    const dialog =
      document.querySelector("[role='dialog'][data-offer-composer]") ||
      document.querySelector("[data-offer-composer-root] [role='dialog']") ||
      document.querySelector("#offer-composer-root [role='dialog']");
    if (!dialog) {
      if (DEV) console.warn("[openOffer] composer dialog not detected; using legacy fallback");
      fallbackOffer(c, "no-dialog");
    }
  }, 180);
}

function fallbackOffer(c: UserCard, reason: string) {
  if (import.meta.env.DEV) console.debug("[fallbackOffer]", reason, c.id);
  setOfferCard(c);
  setOfferMessage("");
  setOfferPrice("");
  setOfferOpen(true);
  toast({ description: "Opening legacy offer dialog." });
}

// DEV helper
if (import.meta.env.DEV && typeof window !== "undefined") {
  (window as any).__testOfferFirst =
    (window as any).__testOfferFirst ||
    (() => {
      const btn = document.querySelector<HTMLButtonElement>("[data-offer-card]");
      if (!btn) return console.log("No offer card button found");
      btn.click();
    });
}
  // ===== NEW: Helpers for public binders/decks =====
  function chunk<T>(arr: T[], n = 10): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
    return out;
  }

   async function fetchCardsByIds(ownerUid: string, ids: string[]): Promise<UserCard[]> {
    if (!ids.length) return [];
    // Dedupe
    const unique = Array.from(new Set(ids));
    const out: UserCard[] = [];
    // Simple concurrency control
    const concurrency = 5;
    let i = 0;

    async function worker() {
      while (i < unique.length) {
        const idx = i++;
        const cardId = unique[idx];
        try {
          const snap = await getDoc(doc(db, "users", ownerUid, "cards", cardId));
          if (!snap.exists()) continue;
            // If rule blocked, it would have thrown above. Only public cards get here.
          const v: any = snap.data();
          out.push({
            id: snap.id,
            name: v.name ?? "Unknown",
            set: v.set ?? undefined,
            number: v.number ?? undefined,
            image: v.image ?? undefined,
            foil: Boolean(v.foil),
            condition: v.condition,
            qty: v.qty ?? 1,
            status: v.status ?? "keep",
            price: v.price,
            createdAt: v.createdAt,
            scryfall_id: v.scryfall_id,
          });
        } catch (e: any) {
          // Permission or transient error; skip silently
          if (process.env.NODE_ENV === "development") {
            console.debug("[binder/deck] skip card", cardId, e?.code || e?.message);
          }
        }
      }
    }

    const workers = Array.from({ length: Math.min(concurrency, unique.length) }, () => worker());
    await Promise.all(workers);
    return out;
  }

  // NEW: load previews (first N)
  async function loadBinderPreview(uid: string, binderId: string, ids: string[]) {
    if (binderCards[binderId]) return;
    const previewIds = ids.slice(0, 9);
    const cards = await fetchCardsByIds(uid, previewIds);
    setBinderCards(prev => ({ ...prev, [binderId]: cards }));
  }

  async function loadDeckHighlight(uid: string, deckId: string, ids: string[]) {
    if (deckCards[deckId]) return;
    // Commander heuristic: look for first id; adjust if you store commanderUserCardId etc.
    const highlightIds = ids.slice(0, 1);
    const cards = await fetchCardsByIds(uid, highlightIds);
    setDeckCards(prev => ({ ...prev, [deckId]: cards }));
  }

  // NEW: expand loaders
  async function expandBinder(uid: string, binderId: string) {
    const allIds = binderAssignments[binderId] || [];
    if (allIds.length && (!binderCards[binderId] || binderCards[binderId].length < allIds.length)) {
      const cards = await fetchCardsByIds(uid, allIds);
      setBinderCards(prev => ({ ...prev, [binderId]: cards }));
    }
    setExpandedBinders(prev => ({ ...prev, [binderId]: !prev[binderId] }));
  }

  async function expandDeck(uid: string, deckId: string) {
  const opening = !expandedDecks[deckId];
  if (opening) {
    const meta = pubDecks.find(d => d.id === deckId);
    const commanderId = meta?.commanderId || null;
    const snapshot = meta?.commanderSnapshot;
    const slotIds = deckAssignments[deckId] || [];

    const allIds = commanderId && !slotIds.includes(commanderId)
      ? [commanderId, ...slotIds]
      : slotIds.slice();

    const missing = allIds.filter(id => !cards.some(c => c.id === id));
    if (missing.length) {
      await fetchMissingCardDocs(uid, missing);
    }

    let full = allIds
      .map(id => cards.find(c => c.id === id))
      .filter(Boolean) as UserCard[];

    if (commanderId && !full.some(c => c.id === commanderId) && snapshot) {
      const s = snapshot;
      const pseudo: UserCard = {
        id: commanderId,
        name: s.name || s.cardName || "Commander",
        set: s.set || s.expansion || undefined,
        number: s.number || s.collector_number || undefined,
        image: s.image || s.img || s.imageUrl || s.art || "/images/cardback.png",
        foil: !!(s.foil ?? s.isFoil),
        condition: s.condition || undefined,
        qty: 1,
        status: "trade",
        price: s.price ?? s.listingPrice ?? undefined,
        createdAt: s.createdAt || s.updatedAt || undefined,
        scryfall_id: s.scryfall_id || s.scryfallId || s.id || undefined,
      };
      full = [pseudo, ...full];
    }

    if (commanderId) {
      full = [
        ...full.filter(c => c.id === commanderId),
        ...full.filter(c => c.id !== commanderId),
      ];
    }

    setDeckCards(prev => ({ ...prev, [deckId]: full }));
  }
  setExpandedDecks(prev => ({ ...prev, [deckId]: !prev[deckId] }));
  try {
    const q = new URLSearchParams(searchParams);
    if (opening) q.set("deck", deckId); else q.delete("deck");
    setSearchParams(q, { replace: true });
  } catch {}
}

  /* ---- NEW: Deck share / export helpers ---- */
  function deckShareUrl(deckId: string) {
    return `${window.location.origin}${location.pathname}?deck=${deckId}`;
  }
  function buildDeckExportText(meta: { id: string; name: string }, cards: UserCard[]) {
    const lines: string[] = [];
    lines.push(`# Deck: ${meta.name}`);
    lines.push(`# ID: ${meta.id}`);
    lines.push(`# Exported: ${new Date().toISOString()}`);
    lines.push("");
    const counts = new Map<string, { c: UserCard; q: number }>();
    for (const c of cards) {
      const key = `${c.name}|${c.set}|${c.number}|${c.foil ? "foil" : ""}`;
      const e = counts.get(key);
      const q = c.qty ?? 1;
      if (e) e.q += q; else counts.set(key, { c, q });
    }
    for (const { c, q } of counts.values()) {
      const setPart = c.set ? ` (${c.set}${c.number ? ` #${c.number}` : ""})` : "";
      lines.push(`${q} ${c.name}${setPart}${c.foil ? " *FOIL*" : ""}`);
    }
    lines.push("");
    return lines.join("\n");
  }
  async function shareDeck(deckId: string) {
    const url = deckShareUrl(deckId);
    try {
      if (navigator.share) {
        await navigator.share({ title: "Deck", url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast({ description: "Deck link copied" });
      } else {
        window.prompt("Copy deck link:", url);
      }
    } catch {}
  }
  async function exportDeck(deckId: string) {
  const meta = pubDecks.find(d => d.id === deckId);
  const list = deckCards[deckId] || [];
  if (!meta || list.length === 0) {
    toast({ description: "Open deck first to load cards" });
    return;
  }

  // If not fully expanded, try to expand to ensure all cards loaded
  if (list.length < (deckAssignments[deckId]?.length || 0)) {
    toast({ description: "Expand deck to export full list." });
    return;
  }

  // Group by (name,set,number,foil) and sum qty
  type Key = string;
  const groups = new Map<Key, { qty: number; c: UserCard }>();
  for (const c of list) {
    const q = c.qty ?? 1;
    const key = `${(c.name||"").toUpperCase()}|${(c.set||"").toUpperCase()}|${c.number||""}|${c.foil?"F":""}`;
    const g = groups.get(key);
    if (g) g.qty += q;
    else groups.set(key, { qty: q, c });
  }

  // Build lines: AMOUNT CARDNAME (SETCODE) NUMBER *F*
  // number optional; omit trailing spaces if missing
  const lines: string[] = [];
  const sorted = Array.from(groups.values()).sort((a,b) =>
    a.c.name.localeCompare(b.c.name, undefined, { sensitivity: "base" })
  );
  for (const { qty, c } of sorted) {
    const setCode = (c.set || "").toUpperCase();
    const num = c.number ? ` ${c.number}` : "";
    const foil = c.foil ? " *F*" : "";
    lines.push(`${qty} ${c.name}${setCode ? ` (${setCode})` : ""}${num}${foil}`);
  }

  const exportText = lines.join("\n");

  try {
    await navigator.clipboard.writeText(exportText);
    toast({ description: "Deck list copied" });
  } catch {
    window.prompt("Copy deck list:", exportText);
  }
}


   function deckAllHidden(ids: string[], loaded: UserCard[]) {
    return ids.length > 0 && loaded.length === 0;
  }
  /* ---- NEW: Auto open deck via ?deck= param once assignments loaded ---- */
  useEffect(() => {
    if (!deckSlugParam || !state.uid) return;
    // Wait until we have at least one deck or confirmed empty
    if (!pubDecks.length) return;
    // Find deck by stored slug OR regenerated slug from name
    const match = pubDecks.find(
      d =>
        d.slug === deckSlugParam ||
        (d.name && buildDeckSlug(d.name) === deckSlugParam)
    );
    if (!match) return; // no matching deck
    // If already expanded, nothing to do
    if (expandedDecks[match.id]) return;
    // Ensure assignments present before expand
    if (!deckAssignments[match.id]) return;
    void expandDeck(state.uid, match.id);
    // Also sync query param (?deck= deckId) for existing logic
    try {
      const q = new URLSearchParams(searchParams);
      q.set("deck", match.id);
      setSearchParams(q, { replace: true });
    } catch {}
  }, [
    deckSlugParam,
    state.uid,
    pubDecks,
    deckAssignments,
    expandedDecks,
    searchParams,
    setSearchParams
  ]);

  // --- Resolve profile location (geocode) ---
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!state.uid) return;
      if (!myLoc) return;
      if (!lgsLat && !lgsLng) return;

      try {
        const lat = lgsLat ?? myLoc.lat;
        const lng = lgsLng ?? myLoc.lng;
        const d = await fetch(
          `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&apiKey=${import.meta.env.VITE_GEOAPIFY_KEY}`,
          { method: "GET", mode: "cors" }
        );
        const json = await d.json();
        if (!alive) return;
        if (json.features?.length) {
          const f = json.features[0];
          const props = f.properties;
          const city = props.city || props.town || props.village || props.hamlet || null;
          const state = props.state || props.region || null;
          const country = props.country || null;
          const zip = props.postcode || props.zip || null;
          const formatted = [city, state, country].filter(Boolean).join(", ");
          setMsg(`${formatted} ${zip ? `• ${zip}` : ""}`);
        } else {
          setMsg(null);
        }
      } catch (e) {
        console.warn("Geocode reverse lookup failed:", e);
        setMsg(null);
      }
    })();
    return () => { alive = false; };
  }, [myLoc, lgsLat, lgsLng, state.uid]);

  /* ---------- Render ---------- */
  if (state.loading) {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <p>Loading…</p>
      </main>
    );
  }

  if (state.error === "reserved") return <Navigate to="/" replace />;

  if (state.error === "not-found") {
    const isAuthed = !!auth.currentUser;
    async function onClaimThis() {
      if (!auth.currentUser) {
        toast({ variant: "info", description: "Sign in first" });
        return;
      }
      try {
        const normalized = slug.trim().toLowerCase().replace(/\s+/g, "");
        await claimUsername(auth.currentUser.uid, normalized);
        location.reload();
      } catch (e: unknown) {
        if (e instanceof Error) {
          toast({ variant: "info", description: e.message ?? "Could not claim" });
        } else {
          toast({ variant: "info", description: "Could not claim" });
        }
      }
    }
    return (
      <main className="mx-auto max-w-3xl p-6">
        <Card className="bg-gray-900 text-gray-100 border border-gray-700 shadow-lg">
          <CardHeader>
            <CardTitle className="text-white">@{slug}</CardTitle>
            <CardDescription className="text-gray-300">
              This username isn’t taken yet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isAuthed && <SignIn />}
            <button className="btn-pixel btn-pixel-primary" onClick={onClaimThis}>
              {isAuthed ? "Claim this username" : "Sign in to claim"}
            </button>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (state.error) {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <p>Oops: {state.error}</p>
      </main>
    );
  }

  /* ---------- Render ---------- */
  return (
    <main className="mx-auto max-w-5xl p-4 sm:p-6">
      <section className="relative mt-2 mb-4 rounded-xl border border-slate-700/60 bg-[#0c0a1e] shadow-lg overflow-hidden">
        {/* Name bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 pt-5">
          <h1 className="text-lg sm:text-xl font-bold text-indigo-100 truncate">
            {headerName}
          </h1>
          <div className="flex items-center gap-2 shrink-0">
            {locationString && (
              <span className="text-xs sm:text-sm text-sky-200/90">{locationString}</span>
            )}
            <Button
              variant="outline"
              onClick={openShareProfile}
              aria-label="Share profile"
              className="border-sky-500/60 text-sky-200 hover:bg-sky-400/10"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 mr-1" fill="currentColor" aria-hidden="true">
                <path d="M18 16a3 3 0 0 0-2.24 1.04l-6.5-3.25a3.04 3.04 0 0 0 0-3.58l6.5-3.25A3 3 0 1 0 15 5a3 3 0 0 0 .26 1.22l-6.5 3.25a3 3 0 1 0 0 5.06l6.5 3.25c-.17.38-.26.8-.26 1.22a3 3 0 1 0 3-3Z" />
              </svg>
              Share
            </Button>
            {!isOwnProfile ? (
              auth.currentUser ? (
                <>
                  {isLgs ? (
                    <Button
                      onClick={() => {
                        const mail = lgsEmail ?? p.email ?? p.contact_email ?? "";
                        if (mail) window.location.href = `mailto:${mail}`;
                        else toast({ description: "No email listed for this store." });
                      }}
                    >
                      Email
                    </Button>
                  ) : (
                    <Button onClick={handleMessage}>Message</Button>
                  )}
                  {!isLgs && !isOwnProfile && auth.currentUser && state.uid && (
                    <FollowButtonV3
                      targetUid={state.uid}
                      targetPrivacy={p.follow_privacy}
                      targetUsername={p.username || slug}
                      targetAvatarUrl={p.avatar_url}
                    />
                  )}
                </>
              ) : (
                <SignIn />
              )
            ) : null}
          </div>
        </div>

        {/* Art box / avatar */}
        <div className="px-4">
          <div className="mx-auto max-w-[720px] py-8 grid place-items-center">
            <div className="relative w-36 h-36 sm:w-40 sm:h-40 rounded-full shadow-[inset_0_0_90px_rgba(99,102,241,0.18),0_0_0_2px_rgba(56,189,248,0.25)] bg-gradient-to-br from-[#1a123f] to-[#07051a] border border-violet-800/50">
              <button
                type="button"
                onClick={() => (isOwnProfile ? navigate("/settings") : void 0)}
                className={
                  isOwnProfile
                    ? "block w-full h-full focus:outline-none focus:ring-2 focus:ring-sky-400 rounded-full overflow-hidden"
                    : "block w-full h-full pointer-events-none rounded-full overflow-hidden"
                }
                               title={isOwnProfile ? "Open settings" : undefined}
              >
                <img
                  src={
                    p.avatar_url ||
                    (isLgs ? "/images/lgs_default.png" : "/images/cardback.png")
                  }
                  alt={`${p.display_name ?? `@${slug}`} avatar`}
                  className="w-full h-full object-cover"
                />
              </button>
            </div>
          </div>
        </div>

        {/* Type line */}
        <div className="px-4 pb-4 -mt-4">
          {isLgs ? (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-sm sm:text-base font-semibold tracking-wide">
                {locationString ?? "Store location"}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-sm sm:text-base font-semibold tracking-wide">
                Rank — {rankLabel}
              </div>
              <div className="flex items-center gap-2">
                {tradeOn && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md border border-cyan-300/50 text-cyan-200 bg-cyan-400/10 text-xs sm:text-sm select-none">
                    Trade
                  </span>
                )}
                {playOn && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md border border-fuchsia-300/50 text-fuchsia-200 bg-fuchsia-400/10 text-xs sm:text-sm select-none">
                    Play
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bio + preferences */}
        {(isLgs || (vis.bio ?? false) || showCompetitive || showFormats || showCommander || showMeetups || showLgs) && (
          <div className="px-4 py-4 bg-[#0f0b2a] text-indigo-100">
            {p.bio ? <p className="text-sm sm:text-base mb-4">{p.bio}</p> : null}

            {(showCompetitive || showFormats) && (
              <div className="mb-4">
                <div className="font-semibold text-sky-200 mb-1">Competitive Preferences</div>
                {showCompetitive ? (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {competitive.map((c) => (
                      <Chip key={c}>{c}</Chip>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs opacity-70 mb-2">None listed</div>
                )}

                <div className="font-semibold text-sky-200">Play Formats</div>
                {showFormats ? (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {p.play_formats!.map((f) => (
                      <Chip key={f}>{f}</Chip>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs opacity-70 mt-1">None listed</div>
                )}
              </div>
            )}

            {showCommander && (
              <div className="mb-4">
              <div className="font-semibold text-sky-200 mb-1">Commander / EDH</div>
              <div className="flex flex-wrap gap-2">
                {commanderBrackets.map((b) => (
                  <Chip key={b}>
                    Bracket {b} – {BRACKET_LABELS[b]}
                  </Chip>
                ))}
              </div>
            </div>
            )}

            {/* If this is an LGS, show the full store block */}
            {isLgs ? (
              <div>
                <div className="mt-4 p-3 bg-[#081027] rounded-md border border-slate-700 text-slate-100">
                  <div className="font-semibold text-sky-200 mb-2">Store Info</div>
                  <div className="text-sm text-slate-200 mb-1">
                    <span className="font-medium">Address:</span>{" "}
                    {storeAddrLine || <span className="opacity-70">Not listed</span>}
                  </div>
                  <div className="text-sm text-slate-200 mb-1">
                    <span className="font-medium">Phone:</span>{" "}
                    {lgsPhone || <span className="opacity-70">Not listed</span>}
                  </div>
                  <div className="text-sm text-slate-200 mb-1">
                    <span className="font-medium">Website:</span>{" "}
                    {lgsWebsite ? (
                      <a
                        href={String(lgsWebsite).startsWith("http") ? String(lgsWebsite) : `https://${lgsWebsite}`}
                        target="_blank"
                        rel="noreferrer"
                        className="underline break-all"
                      >
                        {lgsWebsite}
                      </a>
                    ) : (
                      <span className="opacity-70">Not listed</span>
                    )}
                  </div>
                  <div className="text-sm text-slate-200">
                    <span className="font-medium">Email:</span>{" "}
                    {lgsEmail ? (
                      <a href={`mailto:${lgsEmail}`} className="underline">
                        {lgsEmail}
                      </a>
                    ) : (
                      <span className="opacity-70">Not listed</span>
                    )}
                  </div>
                  <div className="text-sm text-slate-200 mt-2">
                    <span className="font-medium">Location:</span>{" "}
                    {lgsLat != null && lgsLng != null ? `${lgsLat}, ${lgsLng}` : <span className="opacity-70">Not listed</span>}
                  </div>
                </div>
              </div>
            ) : (showMeetups || showLgs) ? (
               <div>
                 <div className="mb-4">
                   <div className="font-semibold text-sky-200 mb-1">Meet Up Preferences</div>
                   {showMeetups ? (
                     <div className="flex flex-wrap gap-2 mb-2">
                       {p!.meetup_types!.map((t) => (
                         <Chip key={t}>{t}</Chip>
                       ))}
                     </div>
                   ) : (
                     <div className="text-xs opacity-70 mb-2">None listed</div>
                   )}

                   <div className="font-semibold text-sky-200">Preferred LGS</div>
                   <div className="mt-1">
                     {showLgs ? <Chip>{p?.preferred_lgs}</Chip> : <span className="text-xs opacity-70">Not shown</span>}
                   </div>
                 </div>
               </div>
             ) : null}
          </div>
        )}

        {/* PT totals box */}
        <div className="pointer-events-none absolute bottom-3 right-3">
          <div className="rounded-lg border-2 border-sky-300/80 bg-gradient-to-br from-[#111827] to-[#0b1220] text-sky-100 shadow px-3 py-1.5 leading-none">
            <span className="font-semibold text-lg align-middle">{marketTotal}</span>
            <span className="mx-1 align-middle">/</span>
            <span className="font-semibold text-lg align-middle">{wishlistTotal}</span>
          </div>
        </div>
      </section>

      {msg && (
        <div className="mt-3 text-sm text-sky-200 bg-black/30 border border-sky-500/30 rounded px-3 py-2">
          {msg}
        </div>
      )}

      {/* Tabs: Collection/Wishlist vs Events (now for ALL profiles) */}
      {(showCollection || showWishlist || events.length > 0) && (
        <div className="mt-6 mb-4 flex items-center gap-2 border-b border-slate-700/60 pb-2">
          <button
            type="button"
            onClick={() => setActiveTab("collection")}
            className={
              activeTab === "collection"
                ? "px-3 py-1.5 rounded-md bg-slate-700 text-white text-sm font-medium"
                : "px-3 py-1.5 rounded-md text-slate-300 hover:bg-slate-700/40 text-sm"
            }
          >
            Collection / Wishlist
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("events")}
            className={
              activeTab === "events"
                ? "px-3 py-1.5 rounded-md bg-slate-700 text-white text-sm font-medium"
                : "px-3 py-1.5 rounded-md text-slate-300 hover:bg-slate-700/40 text-sm"
            }
          >
            Events
            <span className="ml-1 inline-block rounded bg-sky-500/20 px-1.5 py-[2px] text-[10px] font-semibold text-sky-300">
              {events.length}
            </span>
          </button>
          {activeTab === "events" && (
            <div className="ml-auto flex items-center gap-2">
              <input
                type="date"
                value={eventDateFilter ?? ""}
                onChange={(e) => setEventDateFilter(e.target.value || null)}
                className="bg-slate-800 border border-slate-600 text-slate-100 text-xs rounded px-2 py-1"
              />
              {eventDateFilter && (
                <button
                  onClick={() => setEventDateFilter(null)}
                  className="text-xs text-sky-300 hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      )}

     {/* ---------- Collection & Wishlist (shown when tab = collection) ---------- */}
     {activeTab === "collection" && (
       <>
    {/* Binders Section (live from public mirrors) */}
    <section className="mt-8 space-y-4" aria-label="Public binders">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Binders</h2>
        <span className="text-xs text-slate-400">{pubBinders.length} total</span>
      </div>
      {pubBinders.length === 0 && (
        <div className="text-xs opacity-60">No public binders.</div>
      )}
      <div className="grid gap-5 md:grid-cols-2">
        {pubBinders.map(b => {
          const expanded = expandedBinders[b.id];
          const slotIds = binderAssignments[b.id] || [];
          const shownCards = binderCards[b.id] || [];
          const preview = !expanded ? shownCards.slice(0, 9) : shownCards;
          return (
            <div
              key={b.id}
              className="rounded-lg border border-slate-700 bg-slate-800/40 overflow-hidden"
            >
              <button
                type="button"
                onClick={() => state.uid && expandBinder(state.uid, b.id)}
                className="w-full text-left px-3 py-2 flex items-center justify-between border-b border-slate-700/60 hover:bg-slate-700/30 transition"
              >
                <div className="truncate font-medium text-sm text-slate-100">{b.name}</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">
                    {(binderAssignments[b.id] || []).length} cards
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-600/70 text-white">
                    {expanded ? "Hide" : "Open"}
                  </span>
                </div>
              </button>
              <div className={`p-3 ${expanded ? "" : "pb-4"}`}>
                {slotIds.length === 0 && (
                  <div className="text-xs opacity-60">Empty binder.</div>
                )}
                {slotIds.length > 0 && preview.length === 0 && (
                  <div className="text-xs opacity-60">Loading…</div>
                )}
                {preview.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {preview.map(c => (
                      <div
                        key={c.id}
                        className="relative group rounded border border-slate-700 bg-black/40 overflow-hidden"
                      >
                        <div className="w-full aspect-[63/88] bg-black grid place-items-center">
                          <img
                            src={c.image || "/images/cardback.png"}
                            alt={c.name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                          />
                        </div>
                        {c.foil && (
                          <div className="absolute top-1 left-1 text-[9px] px-1 rounded bg-amber-500/80 text-black font-semibold">
                            Foil
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 text-[9px] px-1 py-0.5 bg-black/60 text-slate-100 line-clamp-2">
                          {c.name}
                        </div>
                      </div>
                    ))}
                    {!expanded && preview.length < 9 &&
                      [...Array(Math.max(0, 9 - preview.length))].map((_, i) => (
                        <div
                          key={`ph-${i}`}
                          className="relative rounded border border-dashed border-slate-700/60 bg-slate-900/40 aspect-[63/88]"
                        />
                      ))}

                  </div>
                )}
                {!expanded && slotIds.length > 9 && (
                  <div className="mt-2 text-[11px] text-slate-400">
                    +{Math.max(0, slotIds.length - 9)} more
                  </div>
                )}
              </div>
            </div>
          );
        })}

      </div>
    </section>

    {/* Decks Section (highlight + horizontal scroller on expand) */}
    <section className="mt-10" aria-label="Public decks">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-white">Decks</h2>
        <span className="text-xs text-slate-400">{pubDecks.length} total</span>
      </div>
      {pubDecks.length === 0 && (
        <div className="text-xs opacity-60">No public decks.</div>
      )}
      <div className="flex flex-wrap gap-5">
        {pubDecks.map(dk => {
          const expanded = expandedDecks[dk.id];
          const ids = deckAssignments[dk.id] || [];
          const loaded = deckCards[dk.id] || [];
          const highlight = loaded[0];
          const panelId = `deck-panel-${dk.id}`;
          const commanderName =
            dk.commanderId && highlight && highlight.id === dk.commanderId
              ? highlight.name
              : dk.commanderSnapshot?.name || null;
          return (
            <div key={dk.id} className="flex flex-col">
              <div
                className="relative group cursor-pointer w-[148px]"
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() => state.uid && expandDeck(state.uid, dk.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    state.uid && expandDeck(state.uid, dk.id);
                  }
                }}
              >
                <div className="relative rounded overflow-hidden border border-slate-700 bg-black/40 shadow">
                  <div className="w-full aspect-[63/88] bg-black grid place-items-center">
                    <img
                      src={dk.coverImage || highlight?.image || "/images/cardback.png"}
                      alt={highlight?.name || dk.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  {/* Open/Hide badge */}
                  <div className="absolute top-1 left-1 text-[10px] px-1 py-0.5 rounded bg-slate-800/75 backdrop-blur-[2px] text-slate-100">
                    {expanded ? "Hide" : "Open"}
                  </div>
                  {/* Commander badge if present */}
                  {dk.commanderId && (
                    <div className="absolute top-1 right-1 text-[10px] px-1 py-0.5 rounded bg-indigo-600/85 text-white font-semibold shadow">
                      CMD
                    </div>
                  )}
                   {/* Bottom overlay with 4 lines */}
                  <div className="absolute inset-x-0 bottom-0 pt-6 pb-1 px-1.5 bg-gradient-to-t from-black/90 via-black/55 to-transparent text-[10px] leading-tight text-slate-100 font-medium">
                    <div className="truncate font-semibold text-[11px] mb-[2px]">
                      {dk.name}
                    </div>
                    <div className="truncate opacity-90 mb-[2px]">
                      {dk.format || "Deck"}
                    </div>
                    <div className="truncate flex items-center mb-[2px]">
                      {commanderName ? (
                        <>
                          <span className="truncate">{commanderName}</span>
                          {renderBareColorPips(dk.colors)}
                        </>
                      ) : (
                        renderBareColorPips(dk.colors)
                      )}
                    </div>
                    <div className="opacity-80">
                      {ids.length} card{ids.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
              </div>
              {expanded && (
                <div
                  id={panelId}
                  className="mt-2 w-[min(94vw,780px)] max-w-[780px]"
                >
                  {ids.length === 0 && (
                    <div className="text-[11px] opacity-60 ml-0.5">Empty deck.</div>
                  )}
                  {ids.length > 0 && loaded.length === 0 && (
                    <div className="text-[11px] opacity-60 ml-0.5">
                      Loading deck…
                    </div>
                  )}
                  {loaded.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-1 pr-1 mt-1 scrollbar-thin">
                      {loaded.map(c => (
                        <div
                          key={c.id}
                          className="relative group rounded border border-slate-700 bg-black/40 overflow-hidden flex-shrink-0 w-[96px]"
                          title={c.name}
                        >
                          <img
                            src={c.image || "/images/cardback.png"}
                            alt={c.name}
                            className="w-full aspect-[63/88] object-contain bg-black"
                            loading="lazy"
                          />
                          {dk.commanderId && c.id === dk.commanderId && (
                            <div className="absolute top-1 left-1 rounded bg-yellow-500/90 text-black text-[10px] px-1 font-semibold">
                              Commander
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 text-[9px] px-1 py-0.5 bg-black/60 text-slate-100 line-clamp-2">
                            {c.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {loaded.length > 0 && loaded.length < ids.length && (
                    <div className="text-[10px] text-slate-400 mt-1 ml-0.5">
                      {ids.length - loaded.length} card(s) not visible.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>

    {/* For Sale / Trade (FILTER UPDATED to hide binder/deck cards) */}
    {showCollection && (
      <section className="mt-12 space-y-4" aria-label="Collection">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">For Sale / Trade</h2>
          <div className="flex items-center gap-2">
            {(["all","sale","trade"] as const).map(opt => (
              <button
                key={opt}
                onClick={() => setFilter(opt)}
                className={
                  filter === opt
                    ? "px-3 py-1.5 rounded-md bg-sky-600/70 text-white text-xs font-medium"
                    : "px-3 py-1.5 rounded-md text-slate-300 hover:bg-slate-700/40 text-xs"
                }
              >
                {opt === "all" ? "All" : opt === "sale" ? "For Sale" : "For Trade"}
              </button>
            ))}
            <button
              onClick={() => setHideAssigned(h => !h)}
              className={
                hideAssigned
                  ? "px-3 py-1.5 rounded-md bg-amber-600/70 text-white text-xs font-medium"
                  : "px-3 py-1.5 rounded-md text-slate-300 hover:bg-slate-700/40 text-xs"
              }
              title={
                hideAssigned
                  ? "Showing only standalone cards (click to include deck/binder cards)"
                  : "Including deck/binder cards (click to hide them)"
              }
            >
              {hideAssigned ? "Show All" : "Hide In Decks/Binders"}
            </button>
          </div>
        </div>

        {loadingCards && cards.length === 0 && (
          <div className="text-sm opacity-70">Loading cards…</div>
        )}
        {!loadingCards && visibleMarketCards.length === 0 && (
          <div className="text-sm opacity-70">
            No cards listed (others may be in binders or decks).
          </div>
        )}

        {visibleMarketCards.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {visibleMarketCards.map(c => (
              <button
                key={`${c.id}:${c.status}`}
                onClick={() => openOffer(c)}
                className="group text-left bg-slate-800/60 hover:bg-slate-700/70 border border-slate-700 rounded-lg p-2 flex flex-col gap-2 transition focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-offer-card={c.id}
              >
                <div className="relative w-full aspect-[63/88] overflow-hidden rounded bg-black">
                  <img
                    src={c.image || "/images/cardback.png"}
                    alt={c.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                  {c.foil && (
                    <span className="absolute top-1 left-1 text-[10px] px-1 py-0.5 rounded bg-amber-500/80 text-black font-semibold">
                      Foil
                    </span>
                  )}
                  <span
                    className={
                      c.status === "sale"
                        ? "absolute bottom-1 right-1 text-[10px] px-1 py-0.5 rounded bg-emerald-500/80 text-black font-semibold"
                        : "absolute bottom-1 right-1 text-[10px] px-1 py-0.5 rounded bg-cyan-400/80 text-black font-semibold"
                    }
                  >
                    {c.status === "sale" ? "Sale" : "Trade"}
                  </span>
                </div>

                {/* Bottom section: PriceOverlay, full card width, left-justified */}
                <div className="mt-1 w-full">
                  <PriceOverlay
                    scryfallId={c.scryfall_id || c.id}
                    textScale={0.6}
                    forceBottomThird={false}
                    bottomPaddingPercent={0}
                    className="!static block w-full !max-w-none !justify-start text-left"
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
    )}

    {/* Wishlist section remains unchanged below */}
    {showWishlist && (
      <section className="mt-10 space-y-4" aria-label="Wishlist">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Wishlist</h2>
        </div>

        {loadingWishes && wishes.length === 0 && (
          <div className="text-sm opacity-70">Loading wishlist…</div>
        )}
        {!loadingWishes && wishes.length === 0 && (
          <div className="text-sm opacity-70">Wishlist empty.</div>
        )}

        {wishes.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {wishes.map(w => (
              <div
                key={w.id}
                className="bg-slate-800/60 border border-slate-700 rounded-lg p-2 flex flex-col gap-2"
              >
                <div className="relative w-full aspect-[63/88] overflow-hidden rounded bg-black">
                  <img
                    src={w.image || "/images/cardback.png"}
                    alt={w.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                  <PriceOverlay
                    scryfallId={w.scryfall_id || w.id}
                    textScale={0.5}
                    forceBottomThird
                    bottomPaddingPercent={5}
                    className="!pointer-events-none"
                  />
                </div>
                <div className="text-[11px] font-medium leading-tight line-clamp-2">
                  {w.name}
                </div>
                <div className="text-[10px] opacity-70">
                  {w.set || ""} {w.number ? `• #${w.number}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}

        {(hasMoreWishes || loadingWishes) && (
          <div className="pt-1">
            <Button
              variant="outline"
              size="sm"
              disabled={loadingWishes || !hasMoreWishes}
              onClick={() => loadWishes(false)}
            >
              {loadingWishes ? "Loading…" : hasMoreWishes ? "Load More" : "No More"}
            </Button>
          </div>
        )}
      </section>
    )}
       </>

     )}

      {/* ---------- Events (LGS) ---------- */}
      {activeTab === "events" && (
        <section className="space-y-3 mt-8">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">
              {isLgs ? "Store Events" : "Events"}
            </h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" disabled={eventsLoading} onClick={() => {/* optional refresh hook */}}>
                {eventsLoading ? "Loading…" : "Refresh"}
              </Button>
            </div>
          </div>

            {events.length === 0 ? (
              <div className="text-sm opacity-80">
                {eventsLoading ? "Loading…" : "No events found for this profile."}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filteredEvents.map((ev) => (
                  <div key={ev.id} className="rounded-lg">
                    <EventCard ev={ev as any} isFavorite={false} />
                  </div>
                ))}
              </div>
            )}
        </section>
      )}

      {/* ---------- Quick Message Modal ---------- */}
      {msgOpenModal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quick-message-title"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setMsgOpenModal(false); }}
        >
          <div
            className="w-[min(92vw,560px)] rounded-xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl p-4"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 rounded-full overflow-hidden border border-slate-700 bg-white/5 grid place-items-center">
                  {p.avatar_url ? (
                    <img src={p.avatar_url} alt={p.display_name ?? `@${slug}`} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] text-white/70 font-bold">
                      {slug.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <h3 id="quick-message-title" className="text-base font-semibold truncate">
                    Message @{p.username || slug}
                  </h3>
                  {p.display_name && <div className="text-xs text-slate-300 truncate">{p.display_name}</div>}
                </div>
              </div>
              <button onClick={() => setMsgOpenModal(false)} className="text-slate-300 hover:text-white" aria-label="Close">
                ✕
              </button>
            </div>

            <div className="grid gap-2 mb-3">
              <label className="text-sm">Message</label>
              <textarea
                rows={5}
                className="border rounded px-3 py-2 text-black"
                placeholder="Write a quick message…"
                value={msgBody}
                onChange={(e) => setMsgBody(e.target.value)}
              />
            </div>

            <div className="grid gap-2 mb-2">
              <label className="text-sm">Attachment (optional)</label>
              <input
                type="file"
                accept="image/*,text/plain,application/pdf"
                onChange={(e) => setMsgFile(e.target.files?.[0] ?? null)}
              />
              {msgFile && (
                <div className="text-xs text-slate-300 truncate" title={msgFile.name}>
                  📎 {msgFile.name}
                </div>
              )}
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setMsgOpenModal(false)} disabled={msgSending}>
                Cancel
              </Button>
              <Button onClick={() => void sendQuickMessage()} disabled={msgSending}>
                {msgSending ? "Sending…" : "Send"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Offer Modal (Legacy Fallback) ---------- */}
      {offerOpen && offerCard && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="offer-title"
        >
          <div className="w-[min(92vw,560px)] rounded-xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl p-4">
            <div className="flex items-start justify-between mb-2">
              <h3 id="offer-title" className="text-lg font-semibold">
                {offerCard.status === "sale" ? "Make an Offer" : "Propose a Trade"}
              </h3>
              <button onClick={() => setOfferOpen(false)} className="text-slate-300 hover:text-white" aria-label="Close">
                ✕
              </button>
            </div>

            <div className="flex gap-3 mb-3">
              <img
                src={offerCard.image || "/images/cardback.png"}
                className="h-24 w-18 object-cover rounded border border-slate-700"
                alt={offerCard.name}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{offerCard.name}</div>
                <div className="text-xs opacity-80 truncate">
                  {offerCard.set ?? ""} {offerCard.number ? `• #${offerCard.number}` : ""} {offerCard.foil ? "• Foil" : ""}
                </div>
                <div className="text-xs opacity-80 mt-1">
                  Qty: {offerCard.qty ?? 1} • {offerCard.condition ?? "NM"}
                </div>
                <div className="text-sm mt-1">
                  {offerCard.status === "sale" ? (
                    <span className="font-semibold">{priceLabel(offerCard.price)}</span>
                  ) : (
                    <span className="font-semibold text-cyan-300">For Trade</span>
                  )}
                </div>
              </div>
            </div>

            {offerCard.status === "sale" && (
              <div className="grid gap-2 mb-3">
                <label htmlFor="offer-price" className="text-sm">Your offer (USD, optional)</label>
                <input
                  id="offer-price"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  className="border rounded px-3 py-2 text-black"
                  placeholder={
                    offerCard.price != null
                      ? `Suggested ${Number(offerCard.price).toFixed(2)} (or enter your offer)`
                      : "Enter an amount"
                  }
                  value={offerPrice}
                  onChange={(e) => setOfferPrice(e.target.value)}
                />
              </div>
            )}

            <div className="grid gap-2">
              <label htmlFor="offer-message" className="text-sm">Message</label>
              <textarea
                id="offer-message"
                rows={4}
                className="border rounded px-3 py-2 text-black"
                placeholder={
                  offerCard.status === "sale"
                    ? "Hi! I'm interested in buying this card…"
                    : "Hi! I'm interested in trading for this card. I can offer…"
                }
                value={offerMessage}
                onChange={(e) => setOfferMessage(e.target.value)}
              />
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setOfferOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setOfferOpen(false);
                  toast({ description: `Offer composed for @${p.username || slug}` });
                }}
                disabled={submittingOffer}
              >
                {submittingOffer ? "Sending…" : offerCard.status === "sale" ? "Send Offer" : "Send Trade Proposal"}
              </Button>
            </div>

            <p className="mt-3 text-xs opacity-80">
              The recipient will get a notification and an entry in their Inbox under Offers.
            </p>
          </div>
        </div>
      )}

      {/* ---------- Profile Share Modal (NEW) ---------- */}
      <ProfileShareModal
        open={showProfileShare}
        username={p.username || slug}
        onClose={() => setShowProfileShare(false)}
      />
    </main>
  );
}

